import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { TokenRefreshService } from './token-refresh.service';
import { ErrorHandlerService } from './error-handler.service';
import { redirectToLogin } from '../util/auth-redirect';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLogged: boolean = false;
  route = '';

  private userSubject = new BehaviorSubject<any>(null);
  public user$ = this.userSubject.asObservable();
  public userSignal = toSignal(this.user$);

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenRefreshService: TokenRefreshService,
    private errorHandler: ErrorHandlerService,
  ) {
    this.checkUrlForAccessToken();
    this.isLogged = !!window.localStorage.getItem('access_token');

    // User info will be loaded by route guard before routes activate

    this.tokenRefreshService.startTokenRefresh().subscribe(
      (res: any) => {
        if (res) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          localStorage.setItem('access_token', res.accessToken);
          localStorage.setItem('refresh_token', res.refreshToken);
          localStorage.setItem(
            'token_expiry',
            '' + new Date(res.expiration).getTime(),
          );
          this.tokenRefreshService.calculateIntervalDuration();
        }
      }, // Log success or handle error
      (error) => {
        localStorage.clear();
        this.errorHandler.handleHttpError(error);
      },
    );
  }

  private checkUrlForAccessToken(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');

    if (accessToken) {
      // Save token to localStorage
      localStorage.setItem('access_token', accessToken);
      this.isLogged = true;
      // User info will be loaded by route guard when navigating
      this.loadUserInfo().subscribe();

      // Clean up URL by removing all query parameters
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true,
      });
    } else {
      // If no token in URL and no token in localStorage, redirect to external auth
      const storedToken = localStorage.getItem('access_token');
      if (!storedToken) {
        redirectToLogin();
      }
    }
  }

  login(email: string, password?: string) {
    return this.http.post(`${environment.loginUrl}Account/login`, {
      email,
      password,
    });
  }

  getUserInfo(): Observable<any> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get(`${environment.apiUrl}/UserProfile/GetLoggedInUser`, {
      headers,
    });
  }

  loadUserInfo(forceRefresh = false): Observable<any> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      this.userSubject.next(null);
      return of(null);
    }

    // If user is already loaded, return it immediately
    if (!forceRefresh && this.userSubject.value !== null) {
      return of(this.userSubject.value);
    }

    return this.getUserInfo().pipe(
      map((res: any) => {
        const user = res?.result || res;
        this.userSubject.next(user);
        return user;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading user info:', error);
        this.userSubject.next(null);
        redirectToLogin();
        return of(null);
      }),
    );
  }

  getCurrentUser(): any {
    return this.userSubject.value;
  }

  /**
   * Checks whether the currently loaded user has a specific permission key
   * with a non-None scopeLevel.
   * scopeLevel 'None' means the permission is explicitly revoked → returns false.
   * @param permissionKey - e.g. 'USER_VIEW', 'TESTCASE_CREATE'
   */
  hasPermission(permissionKey: string): boolean {
    const scope = this.getPermissionScope(permissionKey);
    return scope === 'All' || scope === 'Self';
  }

  hasPermissionSignal(permissionKey: string) {
    return computed(() => {
      const user = this.userSignal();
      if (!user || !Array.isArray(user.userPermissions)) {
        return false;
      }
      const perm = user.userPermissions.find(
        (p: { permissionKey: string; scopeLevel: string }) =>
          p.permissionKey === permissionKey
      );
      return perm
        ? perm.scopeLevel === 'All' || perm.scopeLevel === 'Self'
        : false;
    });
  }

  /**
   * Returns the scopeLevel for a given permissionKey, or null if not found.
   * Possible values: 'All' | 'Self' | 'None' | null
   */
  getPermissionScope(permissionKey: string): string | null {
    const user = this.userSubject.value;
    if (!user || !Array.isArray(user.userPermissions)) {
      return null;
    }
    const perm = user.userPermissions.find(
      (p: { permissionKey: string; scopeLevel: string }) =>
        p.permissionKey === permissionKey,
    );
    return perm ? perm.scopeLevel : null;
  }

  validateToken() {
    return this.http.get(`${environment.apiUrl}Account/ValidatedToken`);
  }

  signOut(isIsc?: boolean) {
    this.isLogged = false;
    this.userSubject.next(null);
    redirectToLogin();
  }

  handleError(errorResponse: HttpErrorResponse) {
    if (errorResponse.status === 401) {
      this.signOut();
    }
  }

  refreshSSOToken(token: string, refreshToken: string) {
    return this.http.post(`${environment.apiUrl}Account/refreshSSO`, {
      token,
      refreshToken,
    });
  }

  generateApplicationToken() {
    return this.http.get(
      `${environment.apiUrl}Account/GenerateApplicationToken`,
    );
  }

  getExternalToken(token: string, refreshToken?: string) {
    const data = {
      Token: token,
      RefreshToken: refreshToken,
    };
    return this.http.post(
      `${environment.webAuthUrl}Authentication/generateExternalToken`,
      data,
    );
  }
}
